import { Button, Col, Drawer, Form, Input, Row, Select } from 'antd';
import { Formik } from 'formik';
import { FiCheck } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import type { TopicDrawerProps, TopicType } from '../../Topics.types.ts';
import {
  TOPICS_FORM,
  TOPICS_FORM_SCHEMA,
  TOPICS_INITIAL_FORM_VALUES,
} from '../../Topics.constants.tsx';

import './TopicDrawer.styles.css';
import { useTranslation } from 'react-i18next';
import { getTopicValues } from '../../Topics.helpers.ts';
import { useService } from '../../Topics.hooks.ts';

export default function TopicDrawer({ onClose, open, onSubmit, topicId }: TopicDrawerProps) {
  const { t } = useTranslation();
  const { topic, topics } = useService({ topicId });

  const initialValues = topicId ? getTopicValues(topic) : TOPICS_INITIAL_FORM_VALUES;

  return (
    <Drawer
      title={topicId ? topic?.name : t('pages.topics.create')}
      placement="right"
      closable={false}
      width={500}
      onClose={onClose}
      open={open}
      extra={
        <Row justify="end" align="middle" gutter={8}>
          <span onClick={onClose} style={{ cursor: 'pointer' }}>
            <MdClose fontSize={20} />
          </span>
        </Row>
      }
      footer={
        <Row justify="end" gutter={8}>
          <Col>
            <Button size="large" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </Col>
          <Col>
            <Button
              size="large"
              type="primary"
              icon={<FiCheck />}
              iconPosition="end"
              htmlType="submit"
              form={TOPICS_FORM}
            >
              {t('common.confirm')}
            </Button>
          </Col>
        </Row>
      }
    >
      <Formik
        initialValues={initialValues}
        validationSchema={TOPICS_FORM_SCHEMA}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
          return (
            <Form layout="vertical" onFinish={handleSubmit} id={TOPICS_FORM}>
              <Form.Item
                label={t('form.field.name')}
                validateStatus={touched.name && errors.name ? 'error' : ''}
                help={touched.name && typeof errors.name === 'string' ? errors.name : undefined}
              >
                <Input
                  name="name"
                  variant="underlined"
                  value={values.name}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.order')}
                validateStatus={touched.order && errors.order ? 'error' : ''}
                help={touched.order && typeof errors.order === 'string' ? errors.order : undefined}
              >
                <Input
                  name="order"
                  variant="underlined"
                  value={values.order}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.type')}
                validateStatus={touched.type && errors.type ? 'error' : ''}
                help={touched.type && typeof errors.type === 'string' ? errors.type : undefined}
              >
                <Select
                  variant="underlined"
                  value={values.type}
                  placeholder={t('form.placeholder.select-here')}
                  onChange={(e) => setFieldValue('type', e)}
                  options={[
                    { value: 0, label: t('form.option.without-children') },
                    { value: 1, label: t('form.option.with-children') },
                  ]}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.parent-topic')}
                validateStatus={touched.parentTopicId && errors.parentTopicId ? 'error' : ''}
                help={
                  touched.parentTopicId && typeof errors.parentTopicId === 'string'
                    ? errors.parentTopicId
                    : undefined
                }
              >
                <Select
                  variant="underlined"
                  value={values.parentTopicId}
                  onChange={(e) => setFieldValue('parentTopicId', e)}
                  options={topics?.map((topic: TopicType) => ({
                    value: topic.id,
                    label: topic.name,
                  }))}
                  placeholder={t('form.placeholder.select-here')}
                />
              </Form.Item>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
}
